import React, { useState, useMemo } from 'react';
import { Map, List as ListIcon } from 'lucide-react';
import MapView from './components/MapView';
import ListView from './components/ListView';
import { mockUsers } from './data/users';

const ConceptMatching = () => {
  const [view, setView] = useState('map');
  const [showOnlySharedInterests, setShowOnlySharedInterests] = useState(false);

  // Mock current user for demo
  const currentUser = {
    id: 999,
    name: "Demo User",
    location: "Zurich",
    coordinates: { lat: 47.37, lng: 8.54 },
    interests: ["coffee", "hiking", "music", "art"],
    avatar: "https://ui-avatars.com/api/?name=Demo+User&background=ef4444&color=fff&size=200"
  };

  // Calculate distances and filter users
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  const matchingUsers = useMemo(() => {
    let users = mockUsers.map(user => ({
      ...user,
      distance: calculateDistance(
        currentUser.coordinates.lat,
        currentUser.coordinates.lng,
        user.coordinates.lat,
        user.coordinates.lng
      ),
      avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name)}&background=random&size=200`
    }));

    if (showOnlySharedInterests) {
      users = users.filter(user => 
        user.interests.some(interest => currentUser.interests.includes(interest))
      );
    }

    return users.sort((a, b) => a.distance - b.distance);
  }, [showOnlySharedInterests]);

  // Cluster users by distance for ListView
  const clusters = useMemo(() => {
    return {
      nearby: matchingUsers.filter(u => u.distance < 10),
      close: matchingUsers.filter(u => u.distance >= 10 && u.distance < 30),
      distant: matchingUsers.filter(u => u.distance >= 30)
    };
  }, [matchingUsers]);

  const handleConnectWithUser = (user) => {
    alert(`Demo: This would connect you with ${user.name}`);
  };

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 flex-shrink-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">SwissMeet</h1>
              <p className="text-sm text-gray-500">Concept A: User Matching</p>
            </div>
            
            {/* View Toggle */}
            <div className="flex bg-white rounded-lg shadow-sm border border-gray-200 p-1">
              <button
                onClick={() => setView('map')}
                className={`px-4 py-2 rounded-md flex items-center space-x-2 transition-colors ${
                  view === 'map'
                    ? 'bg-red-600 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Map className="w-4 h-4" />
                <span>Map</span>
              </button>
              <button
                onClick={() => setView('list')}
                className={`px-4 py-2 rounded-md flex items-center space-x-2 transition-colors ${
                  view === 'list'
                    ? 'bg-red-600 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <ListIcon className="w-4 h-4" />
                <span>List</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 overflow-hidden">
        {view === 'map' ? (
          <MapView
            matchingUsers={matchingUsers}
            handleConnectWithUser={handleConnectWithUser}
            showOnlySharedInterests={showOnlySharedInterests}
            setShowOnlySharedInterests={setShowOnlySharedInterests}
            currentUser={currentUser}
          />
        ) : (
          <div className="h-full overflow-y-auto">
            <ListView
              clusters={clusters}
              currentUser={currentUser}
              handleConnectWithUser={handleConnectWithUser}
              showOnlySharedInterests={showOnlySharedInterests}
              setShowOnlySharedInterests={setShowOnlySharedInterests}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ConceptMatching;
